<template>
    <div class="mh-90 h-90">
        <div v-if="flow._id && !error" class="page-wrapper">
            <div class="page-body">
                <div class="row">
                    <div class="col">
                        <div class="card">
                            <!-- Title and Description -->
                            <div class="card-header">
                                <div class="d-flex gap-2 flex-column mw-100 w-100">
                                    <!-- Title -->
                                    <div class="d-flex position-relative">
                                        <div class="d-flex align-items-center gap-2 mw-100">
                                            <i class="fs-1 ti" :class="'ti-' + flow.icon"></i>
                                            <h3 class="card-title mw-100 title-clamp">
                                                {{ flow.chatTitle }}
                                            </h3>
                                        </div>
                                        <i
                                            class="fs-1 ti ti-help-hexagon align-self-end cursor-pointer position-absolute top-0 end-0"
                                            data-bs-trigger="hover"
                                            data-bs-toggle="modal"
                                            title="Informações sobre o chat"
                                            data-bs-target="#sessionInfoModal"
                                        ></i>
                                    </div>

                                    <!-- Modal -->
                                    <div id="sessionInfoModal" class="modal" tabindex="-1">
                                        <div class="modal-dialog" role="document">
                                            <div class="modal-content">
                                                <div class="modal-header">
                                                    <h5 class="modal-title">Informações sobre o chat</h5>
                                                    <button
                                                        type="button"
                                                        class="btn-close"
                                                        data-bs-dismiss="modal"
                                                        aria-label="Close"
                                                    ></button>
                                                </div>
                                                <div class="modal-body">
                                                    <div class="row mb-2">
                                                        <h3 class="mb-2">Sessão</h3>
                                                        <p class="mb-1">
                                                            {{
                                                                chatSessionId
                                                                    ? "ID da Sessão: " + chatSessionId
                                                                    : "Habilite o uso de memória no conector AI Chat Trigger e interaja com o chat para iniciar uma nova sessão."
                                                            }}
                                                        </p>
                                                        <p v-if="chatSessionId && chatExpiresAt" class="mb-1">
                                                            Expira em: {{ formatDate(chatExpiresAt) }}
                                                            <i
                                                                class="ti ti-alert-triangle fs-3"
                                                                data-bs-toggle="tooltip"
                                                                title="Após o tempo de expiração, uma nova sessão de chat será criada. Mensagens anteriores não serão recuperadas."
                                                            ></i>
                                                        </p>
                                                    </div>

                                                    <div class="row mb-2">
                                                        <h3 class="mb-2">
                                                            Ferramentas associadas ao flow
                                                            <span v-if="flow.toolsCount > 0"
                                                                >({{ flow.toolsCount }})</span
                                                            >
                                                        </h3>
                                                        <div v-if="flow.toolsCount === 0">
                                                            <p>Nenhuma ferramenta está sendo usada neste fluxo.</p>
                                                        </div>
                                                        <div v-else>
                                                            <ul v-if="flow.tools" class="list-unstyled">
                                                                <li
                                                                    v-for="[toolId, toolName] in Object.entries(
                                                                        flow.tools
                                                                    )"
                                                                    :key="toolId"
                                                                >
                                                                    <a :href="'/flows/' + toolId" target="_blank">{{
                                                                        toolName
                                                                    }}</a>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Description -->
                                    <div>
                                        <p
                                            class="card-subtitle text-muted paragraph-clamp"
                                            data-bs-toggle="tooltip"
                                            :title="flow.aiChatTriggerDescription"
                                        >
                                            {{ flow.aiChatTriggerDescription }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div class="card-body d-flex flex-column gap-2">
                                <!-- Flow Info -->
                                <div class="row">
                                    <div class="col">
                                        <span class="text-muted">Flow </span>
                                        <a :href="'/flows/' + flow._id" target="_blank">{{ flow.flowName }}</a>
                                    </div>

                                    <div class="col-auto">
                                        <span
                                            :class="flow.group === null ? 'bg-gray-lt' : 'bg-blue-lt'"
                                            class="badge d-flex"
                                            >{{ flow.group || "Sem grupo" }}</span
                                        >
                                    </div>
                                </div>
                            </div>

                            <!-- Messages Container -->
                            <div class="card-body bg-gray-100">
                                <!-- Messages History -->
                                <div class="d-flex flex-column h-100 messages">
                                    <!-- No message history state -->
                                    <div v-if="messagesHistory.length === 0">
                                        <div class="text-muted text-center p-3">
                                            <div>
                                                <h3 class="m-0 mb-1 title-clamp">{{ flow.chatTitle }}</h3>
                                                <p class="m-0">
                                                    Envie uma mensagem para iniciar a conversa com o agente
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Messages List -->
                                    <div ref="messagesContainer" class="flex-grow-1 overflow-auto p-2">
                                        <ul class="list-unstyled">
                                            <li
                                                v-for="(msg, idx) in messagesHistory"
                                                :key="idx"
                                                :class="[
                                                    'mb-3 d-flex',
                                                    msg.role === 'user'
                                                        ? 'justify-content-end'
                                                        : 'justify-content-start'
                                                ]"
                                            >
                                                <div
                                                    :class="[
                                                        msg.role === 'user' && !msg.from_error
                                                            ? 'bg-blue-lt user-message'
                                                            : msg.role === 'assistant' && !msg.from_error
                                                              ? 'bg-white border border-gray text-dark ai-message'
                                                              : 'bg-red-lt text-white ai-message'
                                                    ]"
                                                    class="p-2 px-3 rounded"
                                                >
                                                    <div>
                                                        <small
                                                            class="m-0 mb-2"
                                                            :class="msg.from_error ? 'text-red' : 'text-blue'"
                                                            ><i
                                                                class="fs-4 ti pe-1"
                                                                :class="
                                                                    msg.role === 'user'
                                                                        ? 'ti-user'
                                                                        : msg.role === 'assistant' && !msg.from_error
                                                                          ? 'ti-sparkles'
                                                                          : 'ti-alert-triangle'
                                                                "
                                                            ></i
                                                            ><strong>{{
                                                                msg.role === "user"
                                                                    ? "Você"
                                                                    : msg.role === "assistant" && !msg.from_error
                                                                      ? flow.chatTitle
                                                                      : "Erro ao executar instrução"
                                                            }}</strong></small
                                                        >
                                                    </div>
                                                    <div
                                                        v-if="typeof msg.content === 'string'"
                                                        v-html="parseMarkdown(msg.content)"
                                                    ></div>
                                                    <div v-else>
                                                        <vue-json-pretty
                                                            :data="msg.content"
                                                            :expand-depth="2"
                                                            :show-line-numbers="false"
                                                        ></vue-json-pretty>
                                                    </div>
                                                </div>
                                            </li>

                                            <!-- Loader -->
                                            <li v-if="isLoading" class="mb-3">
                                                <div class="message-loader">
                                                    <div class="message-loader-anim"></div>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            <div class="card-footer bg-white">
                                <!-- Message Input -->
                                <form method="post" @submit.prevent="handleSubmit">
                                    <div class="message-input-container">
                                        <textarea
                                            id="messageInput"
                                            ref="messageInputRef"
                                            v-model="messageInput"
                                            type="text"
                                            class="chat-message-input form-control rounded"
                                            placeholder="Digite sua mensagem... Use Shift+Enter para quebrar linha"
                                            autocomplete="off"
                                            rows="1"
                                            :disabled="isLoading"
                                            @keydown.enter="onEnterPress"
                                            @input="autoResizeTextarea"
                                        ></textarea>
                                        <button
                                            type="submit"
                                            class="btn-send-message"
                                            :disabled="isLoading || messageInput.trim() === ''"
                                        >
                                            <i class="ti ti-send m-0"></i>
                                        </button>
                                    </div>
                                </form>
                                <!-- Warning -->
                                <small class="text-muted m-0 pt-3 text-center d-block">
                                    Agentes de IA podem cometer erros. Considere verificar informações importantes antes
                                    de tomar decisões com base nas respostas.
                                </small>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Notfound page -->
        <div v-else-if="error === 'NotFound'">
            <div class="page-body">
                <div class="row g-2">
                    <div class="col">
                        <div class="card">
                            <div class="card-body">
                                <div
                                    class="d-flex flex-column error-container align-items-center justify-content-center"
                                >
                                    <h1 class="not-found-title">404</h1>
                                    <h1 class="not-found-title">Flow não encontrado</h1>
                                    <div class="col-auto mt-3">
                                        <a id="backButton" class="btn btn-primary" :href="`/chats`">
                                            &larr; Voltar para lista de chats
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment"
import { marked } from "marked"
import DOMPurify from "dompurify"
import VueJsonPretty from "vue-json-pretty"
import "vue-json-pretty/lib/styles.css"

import { store } from "../store"
import Api from "../services/api"
import EngineApi from "../services/engine-api"
import EventBus from "@/services/event-bus"

export default {
    name: "ChatsViewPage",
    components: {
        VueJsonPretty
    },
    data() {
        return {
            flow: {},
            flow_id: null,
            messagesHistory: [],
            chatSessionId: null,
            chatExpiresAt: null,
            messageInput: "",
            currentPage: 1,
            showPerPage: 10,
            paginationKey: null,
            isLoading: false,
            error: null,
            minTextareaHeight: 60,
            maxTextareaHeight: 150
        }
    },
    watch: {
        messageInput() {
            this.$nextTick(() => {
                this.autoResizeTextarea()
            })
        }
    },
    async mounted() {
        store.showSidebar = true
        store.showHeader = true

        this.flow_id = this.$route.params.flow_id

        this.loadFlow()

        const existingChatSessions = JSON.parse(localStorage.getItem("user_chat_sessions")) || []
        const chatSessionFromFlow = existingChatSessions.find((session) => session.flow_id === this.flow_id)
        const timestampNow = Date.now()

        if (!chatSessionFromFlow || chatSessionFromFlow.expires_at < timestampNow) {
            return
        }

        await this.loadMessagesHistory(chatSessionFromFlow.chat_session_id)

        this.$nextTick(() => {
            this.autoResizeTextarea()
        })
        this.scrollToBottom()
    },
    methods: {
        async loadMessagesHistory(chatSessionId) {
            try {
                const response = await Api.aiChatMessages.getByChatSessionId(chatSessionId)

                if (!response.data) {
                    return
                }

                this.chatSessionId = response.data?.chat_session_id
                this.chatExpiresAt = response.data?.expires_at
                this.messagesHistory = response.data?.messages || []
            } catch (error) {
                console.log("Error loading messages history:", error.message)
                if (error.response && error.response.status === 404) {
                    this.clearLocalStorageEntry(chatSessionId)
                }
                this.messagesHistory = []
            } finally {
                this.scrollToBottom()
                this.$nextTick(() => {
                    this.autoResizeTextarea()
                })
            }
        },

        async sendMessage(e) {
            this.isLoading = true
            EngineApi.axiosInstance._disable_loaders = true
            Api.axiosInstance._disable_loaders = true

            const messageInput = e.target[0].value
            const userMessage = {
                role: "user",
                content: messageInput
            }

            this.messagesHistory.push(userMessage)
            this.messageInput = ""
            this.scrollToBottom()
            this.$nextTick(() => {
                this.autoResizeTextarea()
            })

            const payload = {
                userMessage,
                chatSessionId: this.chatSessionId
            }

            let messageFromChatResponse = {
                role: "assistant",
                content: "Aguardando resposta..."
            }

            let response = null

            try {
                // response comes from the AI Chat Response node (success) / FlowProcessor (error)
                response = await EngineApi.flows.executeWithChat(this.flow_id, payload)
                messageFromChatResponse = response.data?.ai_chat_response

                this.chatSessionId = response.data?.chat_session_id
                this.chatExpiresAt = response.data?.chat_expires_at

                if (this.chatSessionId && this.chatExpiresAt) {
                    this.handleLocalStorageSession()
                }

                this.messagesHistory.push(messageFromChatResponse)
            } catch (error) {
                console.log("Error sending message:", error)
                if (error.response && error.response.status === 404) {
                    this.error = "NotFound"
                    return
                }

                messageFromChatResponse = {
                    role: error.response?.data?.ai_chat_response?.role || "assistant",
                    content:
                        error.response?.data?.ai_chat_response?.content ||
                        "Desculpe, houve um erro ao processar sua mensagem.",
                    from_error: error.response?.data?.ai_chat_response?.from_error || true
                }

                this.chatSessionId = error.response?.data?.chat_session_id
                this.chatExpiresAt = error.response?.data?.chat_expires_at

                if (this.chatSessionId && this.chatExpiresAt) {
                    this.handleLocalStorageSession()
                }

                this.messagesHistory.push(messageFromChatResponse)
            } finally {
                this.isLoading = false
                this.scrollToBottom()
                this.$nextTick(() => {
                    this.autoResizeTextarea()
                })
                Api.axiosInstance._disable_loaders = false
                EngineApi.axiosInstance._disable_loaders = false
            }
        },

        clearLocalStorageEntry(chatSessionId) {
            const existingChatSessions = JSON.parse(localStorage.getItem("user_chat_sessions")) || []
            const updatedChatSessions = existingChatSessions.filter(
                (session) => session.chat_session_id !== chatSessionId
            )
            localStorage.setItem("user_chat_sessions", JSON.stringify(updatedChatSessions))
        },

        handleLocalStorageSession() {
            const existingChatSessions = JSON.parse(localStorage.getItem("user_chat_sessions")) || []
            const chatSessionIndex = existingChatSessions.findIndex((session) => session.flow_id === this.flow_id)

            const expiresAtTimestamp = this.chatExpiresAt ? new Date(this.chatExpiresAt).getTime() : null

            // create new session for flow
            if (chatSessionIndex === -1) {
                existingChatSessions.push({
                    flow_id: this.flow_id,
                    chat_session_id: this.chatSessionId,
                    expires_at: expiresAtTimestamp
                })
                localStorage.setItem("user_chat_sessions", JSON.stringify(existingChatSessions))

                EventBus.emit("message", {
                    type: "success",
                    message: "Nova sessão de chat criada. " + this.chatSessionId
                })

                return
            }

            // update existing session for flow, only if one of the core fields changed
            if (
                existingChatSessions[chatSessionIndex]?.chat_session_id !== this.chatSessionId ||
                existingChatSessions[chatSessionIndex]?.expires_at !== expiresAtTimestamp
            ) {
                const messagePrefix =
                    existingChatSessions[chatSessionIndex]?.expires_at !== expiresAtTimestamp ? "Sessão expirada. " : ""

                existingChatSessions[chatSessionIndex].chat_session_id = this.chatSessionId
                existingChatSessions[chatSessionIndex].expires_at = expiresAtTimestamp
                const lastUserMessage = this.messagesHistory.pop()

                if (lastUserMessage) {
                    this.messagesHistory = [lastUserMessage]
                }

                localStorage.setItem("user_chat_sessions", JSON.stringify(existingChatSessions))

                EventBus.emit("message", {
                    type: "warning",
                    message: messagePrefix + "Uma nova sessão foi criada."
                })

                return
            }
        },

        async loadFlow() {
            try {
                const response = await Api.flows.getAiChatFlow(this.flow_id)

                if (!response.data) {
                    return
                }
                const flow = response.data

                this.flow = {
                    _id: flow._id,
                    flowName: flow.name,
                    group: this.groupName(flow.groupId),
                    createdAt: this.formatDate(flow.createdAt),
                    updatedAt: this.formatDate(flow.updatedAt),
                    aiChatTriggerDescription: flow.chatDescription || "Sem descrição",
                    chatTitle: flow.chatTitle || "Sem título",
                    icon: flow.chatIcon || "ti ti-cpu",
                    toolsCount: flow.toolsCount || 0,
                    toolNames: flow.toolNames || [],
                    tools: flow.tools || []
                }
            } catch (error) {
                if (error.response && error.response.status === 404) {
                    this.error = "NotFound"
                } else {
                    console.error("Error ao carregar flow:", error)
                }
            }
        },

        parseMarkdown(text) {
            const renderer = new marked.Renderer()

            marked.use({
                extensions: [
                    {
                        name: "paragraph",
                        renderer(tokens) {
                            return `<p style="margin-block: 0.5rem;">${tokens.text}</p>`
                        }
                    }
                ]
            })

            const html = marked(text, { renderer })

            return DOMPurify.sanitize(html)
        },

        formatDate(date) {
            return moment(date).format("DD/MM/YYYY HH:mm:ss")
        },

        updatePage(page) {
            this.currentPage = page
            console.log("updatePage", page)
        },

        groupName(group_id) {
            if (!group_id) return null

            const group = store.groups.find((item) => item._id === group_id)

            return group ? group.name : null
        },

        scrollToBottom() {
            this.$nextTick(() => {
                const container = this.$refs.messagesContainer
                if (container) {
                    container.scrollTop = container.scrollHeight
                }
            })
        },

        autoResizeTextarea() {
            const textarea = this.$refs.messageInputRef
            if (textarea) {
                textarea.style.height = "auto"
                const scrollHeight = textarea.scrollHeight

                let newHeight = Math.max(scrollHeight, this.minTextareaHeight)

                if (newHeight > this.maxTextareaHeight) {
                    textarea.style.height = `${this.maxTextareaHeight}px`
                    textarea.style.overflowY = "auto"
                } else {
                    textarea.style.height = `${newHeight}px`
                    textarea.style.overflowY = "hidden"
                }
            }
        },

        handleSubmit() {
            if (!this.isLoading && this.messageInput.trim() !== "") {
                this.sendMessage({ target: [{ value: this.messageInput }] })
            }
        },

        onEnterPress(event) {
            if (event.shiftKey) {
                return
            }

            event.preventDefault()
            this.handleSubmit()
        }
    }
}
</script>

<style scoped>
small {
    font-size: 0.8rem;
}

.message-input-container {
    position: relative;
    width: 100%;

    .chat-message-input {
        display: block;
        resize: none;
        padding: 20px;
        padding-right: 60px;
        width: 100%;
        font-size: 14px;
        line-height: 1.5;
        border-radius: 100px;
    }

    .btn-send-message {
        position: absolute;
        top: 30px;
        right: 15px;
        transform: translateY(-50%);
        background-color: var(--tblr-primary);
        color: white;
        border: none;
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }
}

.ai-message {
    max-width: 90%;
}

.user-message {
    max-width: 70%;
}

.paragraph-clamp {
    display: -webkit-box;
    --_lines: 3;
    -webkit-line-clamp: var(--_lines);
    line-clamp: var(--_lines);
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.5;
    max-height: calc(1.5em * var(--_lines));
}

.title-clamp {
    display: -webkit-box;
    --_lines: 2;
    -webkit-line-clamp: var(--_lines);
    line-clamp: var(--_lines);
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.5;
    max-height: calc(1.5em * var(--_lines));
}

.messages {
    min-height: 45svh;
    max-height: 45svh;
}

.error-container {
    min-height: 50svh;
    max-height: 100%;
}

.not-found {
    font-size: 3rem;
}

.not-found-title {
    font-size: 2rem;
    font-weight: bold;
    margin-top: 1rem;
}

.message-loader {
    display: inline-block;
    background-color: #fff;
    border: 1px solid #eee;
    padding: 4px 8px;
    border-radius: 5px;

    .message-loader-anim {
        width: 30px;
        aspect-ratio: 2;
        --_g: no-repeat radial-gradient(circle closest-side, var(--tblr-primary) 79%, transparent);
        background:
            var(--_g) 0% 50%,
            var(--_g) 50% 50%,
            var(--_g) 100% 50%;
        background-size: calc(100% / 3) 50%;
        animation: l3 1s infinite linear;
    }
}

@keyframes l3 {
    20% {
        background-position:
            0% 0%,
            50% 50%,
            100% 50%;
    }

    40% {
        background-position:
            0% 100%,
            50% 0%,
            100% 50%;
    }

    60% {
        background-position:
            0% 50%,
            50% 100%,
            100% 0%;
    }

    80% {
        background-position:
            0% 50%,
            50% 50%,
            100% 100%;
    }
}
</style>
